import React, { useState } from "react";
import { Accordion, Flex, Text } from "@chakra-ui/react";
import AppButton from "@/features/line-sheet-sets/app-button";
import useI18nHelper from "@/features/ui/hooks/use-i18n-helper";
import PathPickStartGuideCard from "@/features/pathpick/features/start-guide/components/PathPickStartGuideCard";
import PathPickStartGuideAccordionRow from "@/features/pathpick/features/start-guide/components/PathPickStartGuideAccordionRow";

interface Props {
  onPrev: () => void;
  onNext: () => void;
}

// 전달서 §16 의 6개 항목. 질문/답변 키가 q{n}/a{n} 로 짝을 이룬다.
const FAQ_INDEXES = [1, 2, 3, 4, 5] as const;

/**
 * 자주 묻는 문의.
 *
 * 여러 항목을 동시에 펼칠 수 있다(전달서 §16). 문의 안내와 가이드 완료 안내는
 * 여기가 아니라 다음 `안내 완료` 화면에 있다.
 */
function PathPickStartGuideFaqSection({ onPrev, onNext }: Props) {
  const { t } = useI18nHelper();
  const [expandedIndexes, setExpandedIndexes] = useState<number[]>([]);

  return (
    <Flex flexDirection={"column"} gap={"16px"}>
      {/* 가이드의 마지막 구간임을 알려 주는 안내 (데모와 같은 자리).
          이동 버튼도 여기 우측 하단에 붙는다 — 다른 탭과 같다. */}
      <PathPickStartGuideCard>
        <Flex
          justifyContent={"space-between"}
          alignItems={"flex-end"}
          gap={"12px"}
          flexWrap={"wrap"}
        >
          <Flex flexDirection={"column"} gap={"4px"} minWidth={0}>
            <Text textStyle={"caption-bold"} color={"#1272EF"}>
              {t("path_pick_guide:faq.intro_label")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t("path_pick_guide:faq.intro_body")}
            </Text>
          </Flex>
          {/* 앞뒤가 모두 다른 탭이므로 두 버튼 다 `단계` 라벨이다.
              줄바꿈됐을 때도 오른쪽에 붙도록 marginLeft 로 민다. */}
          <Flex gap={"8px"} flexShrink={0} marginLeft={"auto"}>
            <AppButton variant={"normal"} onClick={onPrev}>
              {t("path_pick_guide:nav.prev_step")}
            </AppButton>
            <AppButton variant={"primary"} onClick={onNext}>
              {t("path_pick_guide:nav.next_step")}
            </AppButton>
          </Flex>
        </Flex>
      </PathPickStartGuideCard>

      <PathPickStartGuideCard
        hasHeaderDivider={true}
        bodyPadding={"0"}
        header={
          <Flex flexDirection={"column"} gap={"6px"}>
            <Text
              textStyle={"body-lg-bold"}
              fontSize={"22px"}
              color={"#1D1D1B"}
            >
              {t("path_pick_guide:faq.title")}
            </Text>
            <Text textStyle={"body-sm-medium"} color={"#6F6F6C"}>
              {t("path_pick_guide:faq.description")}
            </Text>
          </Flex>
        }
      >
        <Accordion
          as={"div"}
          aria-label={t("path_pick_guide:faq.accordion_aria_label")}
          allowMultiple={true}
          index={expandedIndexes}
          onChange={(index) =>
            setExpandedIndexes(typeof index === "number" ? [index] : index)
          }
        >
          {FAQ_INDEXES.map((faqIndex, rowIndex) => (
            <PathPickStartGuideAccordionRow
              key={faqIndex}
              title={t(`path_pick_guide:faq.q${faqIndex}`)}
              isExpanded={expandedIndexes.includes(rowIndex)}
            >
              <Text textStyle={"body-sm-medium"} color={"#444440"}>
                {t(`path_pick_guide:faq.a${faqIndex}`)}
              </Text>
            </PathPickStartGuideAccordionRow>
          ))}
        </Accordion>
      </PathPickStartGuideCard>
    </Flex>
  );
}

export default PathPickStartGuideFaqSection;
